import Vue from 'vue';
import VueRouter from 'vue-router';
import common from './common';
import store from "@/store";
import server from "@/server/request";
import NProgress from 'nprogress';
import 'nprogress/nprogress.css';
import { getLastTeamDetail } from '@/api/myAccount/teamGroup/index.js';
import { IS_SSO } from '@/const/const.js';
import ServerConstants from '@/api/login/server.js';
import CryptoJS from 'crypto-js';

Vue.use(VueRouter);

// ========== PKCE (Proof Key for Code Exchange) ==========
// SPA 公共客户端必须使用 PKCE，SAS 已配置 requireProofKey=true
function base64urlEncode(buffer) {
  let binary = '';
  for (let i = 0; i < buffer.byteLength; i++) binary += String.fromCharCode(buffer[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function generateCodeVerifier() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64urlEncode(array);
}
// HTTP 非 localhost 时 crypto.subtle 不可用，用 crypto-js 作为 fallback
async function generateCodeChallenge(verifier) {
  if (crypto.subtle) {
    // 安全上下文：用原生 Web Crypto API
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return base64urlEncode(new Uint8Array(digest));
  }
  // HTTP 非 localhost fallback：crypto-js SHA-256
  const hash = CryptoJS.SHA256(verifier);
  return CryptoJS.enc.Base64.stringify(hash).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function generateState() {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return base64urlEncode(array);
}
async function buildAuthorizeUrl(redirectPath) {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const state = generateState();
  // 存储 verifier + state 供 callback 时使用
  localStorage.setItem('sso_code_verifier', verifier);
  localStorage.setItem('sso_state', state);
  // 存储用户原始访问路径，登录成功后回跳
  localStorage.setItem('sso_redirect_path', redirectPath || '/');
  const redirectUri = window.location.origin + '/sso/callback';
  return ServerConstants.SSO_AUTHORIZE_URL
    + '?client_id=celelens-app'
    + '&redirect_uri=' + encodeURIComponent(redirectUri)
    + '&response_type=code'
    + '&scope=openid%20profile'
    + '&code_challenge=' + challenge
    + '&code_challenge_method=S256'
    + '&state=' + state;
}

const routes = [
  ...common,
];


// 加载静态路由
const createRouter = () =>
    new VueRouter({
      mode: 'history',
      routes: routes,
    });

// 创建路由
const router = createRouter();

// 重置路由
export function resetRouter() {
  router.matcher = createRouter().matcher;
}
/**
 * 获取最后一次打开的团队详情
 */
async function getLastTeam() {
  if(store.state.user.teamId && store.state.user.teamName) {
    return; // 如果已经有团队信息，则不再请求
  }
  try {
    const response = await getLastTeamDetail();
    if (response.data) {
      const data = response.data;
      store.dispatch('user/changeTeam', {
        id: data.id || '',
        name: data.name || '',
        isOwner: data.self.owner,
        isView: data.self.dataPermissions.includes('1'),
        isEdit: data.self.dataPermissions.includes('2'),
        isDelete: data.self.dataPermissions.includes('3'),
      });
    }
  } catch (error) {
    console.log('获取最后一次打开的团队详情失败:', error);
  }
}



// 捕获push replace中的错误
// 当然在replace中的错误也是可以相同的进行捕获
const originalPush = VueRouter.prototype.push;
const originalReplace = VueRouter.prototype.replace;
// push
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {return originalPush.call(this, location, onResolve, onReject);}
  return originalPush.call(this, location).catch(err => err);
};
// replace
VueRouter.prototype.replace = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {return originalReplace.call(this, location, onResolve, onReject);}
  return originalReplace.call(this, location).catch(err => err);
};



const whiteList = ['/login', '/404', '/', '/migration', '/domain', '/inviteMember', '/sso/callback']; // 定义白名单，所有不受权限控制的页面
router.beforeEach(async(to, from, next) => {
    NProgress.configure({ showSpinner: false });
    NProgress.start();
    store.dispatch('routesList/changeRoutePathA', to.path);
    document.getElementsByTagName('body')[0].classList.remove('global-body');
    if(server.getToken()) {
      document.getElementsByTagName('body')[0].classList.add('global-body');
      await getLastTeam(); // 获取最后一次打开的团队详情
        const flatRouterList = store.state.routesList.flatRouterList;
        const filterArray = flatRouterList.filter(item => item.routePath === to.path);
        if(filterArray.length) {
            const obj = filterArray[0];
            if(obj.disabled || obj.hidden) {
                next('/404');
            }
        }
        // 有token
        next();
    } else {
        // 没有token
        if (IS_SSO && !whiteList.includes(to.path)) {
            NProgress.done();
            buildAuthorizeUrl(to.fullPath).then(url => window.location.replace(url));
        } else if (IS_SSO && to.path === '/login') {
            NProgress.done();
            // /login?redirect=xxx → 提取 redirect 参数；否则默认回首页
            const redirect = to.query.redirect || '/';
            buildAuthorizeUrl(redirect).then(url => window.location.replace(url));
        } else if (whiteList.includes(to.path)) {
            // legacy 模式白名单
            next();
        } else if(to.path) {
            next('/login?redirect=' + to.fullPath);
            NProgress.done();
        } else {
            next();
        }
    }
});
router.afterEach(() => {
    NProgress.done();
});

export default router;
